/**
 * @file エラーコード一元化で、heliport.jsプロジェクトで管理するエラーコードのためのユーティリティ関数群。
 * コマンドラインからの呼び出しに必要となるため、`import` 文へ拡張子を付けている。
 */

import {z} from 'zod';

import {
  CategoriesAndSubCategories,
  ErrorCode,
  errorKeysGeneratedCodePairsSchema
} from '../errorCode.ts';
import errorKeysGeneratedCodePairs from '../models/errorKeysGeneratedCodePairs.json' with {type: 'json'};

import {ensureValueOf, requireValueOf} from './zod.ts';

import type {ErrorKeysGeneratedCodePairs} from '../errorCode.ts';

/**
 * 平坦な `string` 配列同士が、同じ長さで、かつ同じインデックスの要素が一致しているか検査する。
 * @param a
 * @param b
 * @returns すべて一致していれば `true`。
 */
const isEqualStringArray = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return Array.from(a.keys()).every((i) => a[i] === b[i]);
};

/**
 * エラーコードから大カテゴリ部分を除いた部分を返す。
 * @param errorKeysGeneratedCodePairs - `scripts/models/errorKeysGeneratedCodePairs.json`
 * @param keys - 大カテゴリ、中カテゴリ、カテゴリ、操作、原因のエラーコードキー配列。カテゴリ以降はオプション。
 *
 * @throws {Error} 第1引数 `errorKeysGeneratedCodePairs` に存在しないキーが、第2引数 `keys` に含まれていた場合に発生。
 *
 * @returns 中カテゴリ・カテゴリ・操作・原因に対応する部分のコードのみ連結して返す。
 */
export const buildErrorCodeWithoutCategory = (
  errorKeysGeneratedCodePairs: ErrorKeysGeneratedCodePairs,
  keys: [string, string, ...string[]]
): string => {
  const errorCodeParts: string[] = [];
  for (let i = 2; i <= keys.length; i++) {
    const partKeys = keys.slice(0, i);
    const code = errorKeysGeneratedCodePairs.find((codePart) =>
      isEqualStringArray(codePart.keys.slice(0, i), partKeys)
    )?.code;
    if (code === undefined) {
      throw new Error(
        `第1引数「errorKeysGeneratedCodePairs」に存在しないキーの組み合わせ ${JSON.stringify(partKeys)} が、第2引数「keys」で指定されています。\n` +
          'エラーコードのキーを「ErrorCode」へ追加しているか、追加後に「generate-error-codes」を実行しているか、または第2引数「keys」が正しいか確認してください。'
      );
    }
    errorCodeParts.push(code.toString().padStart(2, '0'));
  }
  return errorCodeParts.join('');
};

/**
 * エラーコードを、キーを基に構築して返します。
 * @param category - 大カテゴリのキー。
 * @param subCategory - 中カテゴリのキー。
 * @param subSubCategory - カテゴリのキー。
 * @param operation - 操作のキー。
 * @param cause - 原因のキー。
 * @returns 英大文字3文字 + 数字8文字の文字列。
 */
export const buildErrorCode = <
  TCategory extends keyof typeof ErrorCode,
  TSubCategory extends Extract<keyof (typeof ErrorCode)[TCategory], string>,
  TSubSubCategory extends Extract<keyof (typeof ErrorCode)[TCategory][TSubCategory], string>,
  TOperation extends Extract<
    keyof (typeof ErrorCode)[TCategory][TSubCategory][TSubSubCategory],
    string
  >,
  TCause extends Extract<
    keyof (typeof ErrorCode)[TCategory][TSubCategory][TSubSubCategory][TOperation],
    string
  >
>(
  category: TCategory,
  subCategory: TSubCategory,
  subSubCategory: TSubSubCategory,
  operation: TOperation,
  cause: TCause
): string => {
  return [
    CategoriesAndSubCategories[category].code,
    ...buildErrorCodeWithoutCategory(
      requireValueOf(errorKeysGeneratedCodePairsSchema, errorKeysGeneratedCodePairs),
      [category, subCategory, subSubCategory, operation, cause]
    )
  ].join('');
};

/**
 * {@link buildErrorCode} と同じだが、{@link buildValidateArguments} 内で型エラーが発生しないように、第4引数 `operation`、第5引数 `cause` の型注釈を緩めている。
 * @param category
 * @param subCategory
 * @param subSubCategory
 * @param operation
 * @param cause
 *
 * @throws {ZodError} {@link ErrorCode} に存在しないキーが、第4引数 `operation`、第5引数 `cause` で指定された場合。
 *
 * @returns
 */
export const buildErrorCodeFromAny = <
  TCategory extends keyof typeof ErrorCode,
  TSubCategory extends Extract<keyof (typeof ErrorCode)[TCategory], string>,
  TSubSubCategory extends Extract<keyof (typeof ErrorCode)[TCategory][TSubCategory], string>
>(
  category: TCategory,
  subCategory: TSubCategory,
  subSubCategory: TSubSubCategory,
  operation: string,
  cause: string
): string => {
  ensureValueOf(
    z.object({
      [operation]: z.object({
        [cause]: z.unknown()
      })
    }),
    ErrorCode[category][subCategory][subSubCategory]
  );
  return [
    CategoriesAndSubCategories[category].code,
    ...buildErrorCodeWithoutCategory(
      requireValueOf(errorKeysGeneratedCodePairsSchema, errorKeysGeneratedCodePairs),
      [category, subCategory, subSubCategory, operation, cause]
    )
  ].join('');
};
